import type {
  ActionPermission as SharedActionPermission,
  FeaturePermission as SharedFeaturePermission,
  FeaturePermissionConfig as SharedFeaturePermissionConfig,
  PermissionLevel as SharedPermissionLevel,
  ReportGroupKey as SharedReportGroupKey,
  ReportGroupPermission as SharedReportGroupPermission,
} from "@workspace/shared/permissions";
import {
  hasUsableRolePermission as hasSharedUsableRolePermission,
  resolveEffectivePermissionKeys as resolveSharedEffectivePermissionKeys,
  ACTION_PERMISSION_LABELS as SHARED_ACTION_PERMISSION_LABELS,
  ACTION_PERMISSIONS as SHARED_ACTION_PERMISSIONS,
  ALL_ACTION_PERMISSION_KEYS as SHARED_ALL_ACTION_PERMISSION_KEYS,
  ALL_FEATURE_PERMISSION_KEYS as SHARED_ALL_FEATURE_PERMISSION_KEYS,
  ALL_PERMISSION_KEYS as SHARED_ALL_PERMISSION_KEYS,
  FEATURE_PERMISSIONS as SHARED_FEATURE_PERMISSIONS,
  FEATURES_CONFIG as SHARED_FEATURES_CONFIG,
  PRODUCT_COST_PERMISSION_EXPLICIT as SHARED_PRODUCT_COST_PERMISSION_EXPLICIT,
  QUOTE_PERMISSION_EXPLICIT as SHARED_QUOTE_PERMISSION_EXPLICIT,
  REPORT_GROUPS_CONFIG as SHARED_REPORT_GROUPS_CONFIG,
  STOCK_MOVEMENT_PERMISSION_EXPLICIT as SHARED_STOCK_MOVEMENT_PERMISSION_EXPLICIT,
} from "@workspace/shared/permissions";

export const FEATURE_PERMISSIONS = SHARED_FEATURE_PERMISSIONS;
export const ACTION_PERMISSIONS = SHARED_ACTION_PERMISSIONS;
export const FEATURES_CONFIG = SHARED_FEATURES_CONFIG;
export const REPORT_GROUPS_CONFIG = SHARED_REPORT_GROUPS_CONFIG;
export const ALL_FEATURE_PERMISSION_KEYS = SHARED_ALL_FEATURE_PERMISSION_KEYS;
export const ALL_ACTION_PERMISSION_KEYS = SHARED_ALL_ACTION_PERMISSION_KEYS;
export const ALL_PERMISSION_KEYS = SHARED_ALL_PERMISSION_KEYS;
export const ACTION_PERMISSION_LABELS = SHARED_ACTION_PERMISSION_LABELS;
export const QUOTE_PERMISSION_EXPLICIT = SHARED_QUOTE_PERMISSION_EXPLICIT;
export const STOCK_MOVEMENT_PERMISSION_EXPLICIT =
  SHARED_STOCK_MOVEMENT_PERMISSION_EXPLICIT;
export const PRODUCT_COST_PERMISSION_EXPLICIT =
  SHARED_PRODUCT_COST_PERMISSION_EXPLICIT;
export const hasUsableRolePermission = hasSharedUsableRolePermission;

export type ActionPermission = SharedActionPermission;
export type FeaturePermission = SharedFeaturePermission;
export type PermissionLevel = SharedPermissionLevel;
export type FeaturePermissionConfig = SharedFeaturePermissionConfig;
export type ReportGroupKey = SharedReportGroupKey;
export type ReportGroupPermission = SharedReportGroupPermission;

export const ROUTE_PERMISSIONS = {
  HOME: FEATURE_PERMISSIONS.REPORTS_READ,
  SETTINGS: FEATURE_PERMISSIONS.SETTINGS_WRITE,
  CUSTOMERS: FEATURE_PERMISSIONS.CUSTOMERS_READ,
  IMPORT: FEATURE_PERMISSIONS.IMPORT_WRITE,
  PRODUCTS: FEATURE_PERMISSIONS.PRODUCTS_READ,
  INVENTORY: FEATURE_PERMISSIONS.INVENTORY_READ,
  STOCK_MOVEMENTS: FEATURE_PERMISSIONS.STOCK_MOVEMENTS_READ,
  PRICES: FEATURE_PERMISSIONS.PRICES_READ,
  SALES: FEATURE_PERMISSIONS.SALES_READ,
  QUOTES: FEATURE_PERMISSIONS.QUOTES_READ,
  ORDERS: FEATURE_PERMISSIONS.ORDERS_READ,
  PREPARACION: FEATURE_PERMISSIONS.PREPARACION_WRITE,
  PURCHASES: FEATURE_PERMISSIONS.PURCHASES_READ,
  REPORTS: FEATURE_PERMISSIONS.REPORTS_READ,
  CONTABILIDAD: FEATURE_PERMISSIONS.CONTABILIDAD_READ,
  SUPPLIERS: FEATURE_PERMISSIONS.SUPPLIERS_READ,
  CHECKS: FEATURE_PERMISSIONS.CHECKS_READ,
  TREASURY: FEATURE_PERMISSIONS.TREASURY_READ,
  TRANSFERS: FEATURE_PERMISSIONS.TRANSFERS_READ,
} as const;

export type RoutePermission =
  (typeof ROUTE_PERMISSIONS)[keyof typeof ROUTE_PERMISSIONS];

export const ALL_ROUTE_PERMISSION_KEYS: readonly string[] = [
  ROUTE_PERMISSIONS.HOME,
  ROUTE_PERMISSIONS.SALES,
  ROUTE_PERMISSIONS.QUOTES,
  ROUTE_PERMISSIONS.ORDERS,
  ROUTE_PERMISSIONS.PREPARACION,
  ROUTE_PERMISSIONS.PRODUCTS,
  ROUTE_PERMISSIONS.INVENTORY,
  ROUTE_PERMISSIONS.STOCK_MOVEMENTS,
  ROUTE_PERMISSIONS.PRICES,
  ROUTE_PERMISSIONS.CUSTOMERS,
  ROUTE_PERMISSIONS.PURCHASES,
  ROUTE_PERMISSIONS.SUPPLIERS,
  ROUTE_PERMISSIONS.REPORTS,
  ROUTE_PERMISSIONS.CONTABILIDAD,
  ROUTE_PERMISSIONS.TREASURY,
  ROUTE_PERMISSIONS.CHECKS,
  ROUTE_PERMISSIONS.TRANSFERS,
  ROUTE_PERMISSIONS.IMPORT,
  ROUTE_PERMISSIONS.SETTINGS,
];

/**
 * Maps dashboard route patterns to their required permissions
 * This is used for both middleware validation and page-level checks
 */
export const ROUTE_PERMISSION_MAP: Record<string, RoutePermission> = {
  // Home/Dashboard
  "/[orgSlug]": ROUTE_PERMISSIONS.HOME,

  // Orders
  "/[orgSlug]/pedidos": ROUTE_PERMISSIONS.ORDERS,
  "/[orgSlug]/pedidos-vicus": ROUTE_PERMISSIONS.ORDERS,
  "/[orgSlug]/pedidos-legacy": ROUTE_PERMISSIONS.ORDERS,
  "/[orgSlug]/preparacion": ROUTE_PERMISSIONS.PREPARACION,

  // Sales
  "/[orgSlug]/ventas": ROUTE_PERMISSIONS.SALES,
  "/[orgSlug]/ventas/importar": ROUTE_PERMISSIONS.IMPORT,
  "/[orgSlug]/cobranzas": ROUTE_PERMISSIONS.SALES,
  "/[orgSlug]/suscripciones": ROUTE_PERMISSIONS.SALES,
  "/[orgSlug]/presupuestos": ROUTE_PERMISSIONS.QUOTES,
  "/[orgSlug]/turnos": ROUTE_PERMISSIONS.SALES,
  "/[orgSlug]/remitos": ROUTE_PERMISSIONS.SALES,

  // Products
  "/[orgSlug]/productos": ROUTE_PERMISSIONS.PRODUCTS,

  // Transfers
  "/[orgSlug]/transferencias": ROUTE_PERMISSIONS.TRANSFERS,

  // Inventory
  "/[orgSlug]/inventario": ROUTE_PERMISSIONS.INVENTORY,
  "/[orgSlug]/inventario/movimientos": ROUTE_PERMISSIONS.STOCK_MOVEMENTS,
  "/[orgSlug]/movimientos-inventario": ROUTE_PERMISSIONS.STOCK_MOVEMENTS,
  "/[orgSlug]/recuentos-stock": ROUTE_PERMISSIONS.STOCK_MOVEMENTS,

  // Prices
  "/[orgSlug]/precios": ROUTE_PERMISSIONS.PRICES,

  // Customers
  "/[orgSlug]/clientes": ROUTE_PERMISSIONS.CUSTOMERS,

  // Purchases (includes suppliers and payments)
  "/[orgSlug]/compras": ROUTE_PERMISSIONS.PURCHASES,
  "/[orgSlug]/ordenes": ROUTE_PERMISSIONS.PURCHASES,
  "/[orgSlug]/proveedores": ROUTE_PERMISSIONS.PURCHASES,
  "/[orgSlug]/pagos": ROUTE_PERMISSIONS.PURCHASES,

  // Reports - main hub uses generic REPORTS permission (handled specially)
  "/[orgSlug]/reportes": ROUTE_PERMISSIONS.REPORTS,

  // Accounting
  "/[orgSlug]/contabilidad": ROUTE_PERMISSIONS.CONTABILIDAD,

  // Treasury (includes treasury movements)
  "/[orgSlug]/tesoreria": ROUTE_PERMISSIONS.TREASURY,
  "/[orgSlug]/cheques": ROUTE_PERMISSIONS.CHECKS,
  "/[orgSlug]/movimientos": ROUTE_PERMISSIONS.TREASURY,

  // Import
  "/[orgSlug]/importar": ROUTE_PERMISSIONS.IMPORT,

  // Configuration routes (all sub-routes inherit from /configuracion)
  "/[orgSlug]/configuracion": ROUTE_PERMISSIONS.SETTINGS,
} as const;

export const REPORT_ROUTE_PERMISSION_MAP: Record<string, string> = {
  "/[orgSlug]/reportes/libro-iva-digital":
    FEATURE_PERMISSIONS.REPORTS_IMPOSITIVO_READ,
  "/[orgSlug]/reportes/posicion-iva":
    FEATURE_PERMISSIONS.REPORTS_IMPOSITIVO_READ,
  "/[orgSlug]/reportes/retenciones-percepciones":
    FEATURE_PERMISSIONS.REPORTS_IMPOSITIVO_READ,
  "/[orgSlug]/reportes/ventas-por-actividad":
    FEATURE_PERMISSIONS.REPORTS_IMPOSITIVO_READ,
  "/[orgSlug]/reportes/cumplimiento-isib-caba":
    FEATURE_PERMISSIONS.REPORTS_IMPOSITIVO_READ,
  "/[orgSlug]/reportes/resumen-ventas": FEATURE_PERMISSIONS.REPORTS_VENTAS_READ,
  "/[orgSlug]/reportes/productos-vendidos":
    FEATURE_PERMISSIONS.REPORTS_VENTAS_READ,
  "/[orgSlug]/reportes/mejores-clientes":
    FEATURE_PERMISSIONS.REPORTS_VENTAS_READ,
  "/[orgSlug]/reportes/valorizacion-stock":
    FEATURE_PERMISSIONS.REPORTS_INVENTARIO_READ,
  "/[orgSlug]/reportes/matriz-stock":
    FEATURE_PERMISSIONS.REPORTS_INVENTARIO_READ,
  "/[orgSlug]/reportes/cuenta-corriente":
    FEATURE_PERMISSIONS.REPORTS_FINANCIEROS_READ,
  "/[orgSlug]/reportes/cuentas-pagar":
    FEATURE_PERMISSIONS.REPORTS_FINANCIEROS_READ,
  "/[orgSlug]/reportes/cuentas-cobrar":
    FEATURE_PERMISSIONS.REPORTS_FINANCIEROS_READ,
} as const;

export const ALL_REPORT_GROUP_PERMISSIONS = [
  FEATURE_PERMISSIONS.REPORTS_IMPOSITIVO_READ,
  FEATURE_PERMISSIONS.REPORTS_VENTAS_READ,
  FEATURE_PERMISSIONS.REPORTS_INVENTARIO_READ,
  FEATURE_PERMISSIONS.REPORTS_FINANCIEROS_READ,
] as const;

const ROUTE_TO_FEATURE_MAP: Record<string, string> = {
  "org:route:home": "org:sales:write",
  "org:route:sales": "org:sales:write",
  "org:route:ventas": "org:sales:write",
  "org:route:orders": "org:orders:write",
  "org:route:pedidos": "org:orders:write",
  "org:route:preparacion": "org:preparacion:write",
  "org:route:products": "org:products:write",
  "org:route:productos": "org:products:write",
  "org:route:customers": "org:customers:write",
  "org:route:clientes": "org:customers:write",
  "org:route:purchases": "org:purchases:write",
  "org:route:compras": "org:purchases:write",
  "org:route:suppliers": "org:suppliers:write",
  "org:route:proveedores": "org:suppliers:write",
  "org:route:reports": "org:reports:read",
  "org:route:reportes": "org:reports:read",
  "org:route:contabilidad": "org:contabilidad:read",
  "org:route:treasury": "org:treasury:write",
  "org:route:tesoreria": "org:treasury:write",
  "org:route:checks": "org:checks:write",
  "org:route:cheques": "org:checks:write",
  "org:route:transfers": "org:transfers:write",
  "org:route:movimientos": "org:transfers:write",
  "org:route:settings": "org:settings:write",
  "org:route:configuracion": "org:settings:write",
  "org:route:import": "org:import:write",
  "org:route:importar": "org:import:write",
};

export function normalizePermissionKeys(
  permissions: readonly string[]
): string[] {
  const normalized = new Set<string>(permissions);

  permissions.forEach((permission) => {
    if (permission.startsWith("org:route:")) {
      const featurePermission = ROUTE_TO_FEATURE_MAP[permission];
      if (featurePermission) {
        normalized.add(featurePermission);
      }
    }

    if (permission.includes(":write")) {
      normalized.add(permission.replace(":write", ":read"));
    }

    if (permission.includes(":export")) {
      normalized.add(permission.replace(":export", ":read"));
    }

    if (permission === FEATURE_PERMISSIONS.REPORTS_READ) {
      normalized.add(FEATURE_PERMISSIONS.REPORTS_IMPOSITIVO_READ);
      normalized.add(FEATURE_PERMISSIONS.REPORTS_VENTAS_READ);
      normalized.add(FEATURE_PERMISSIONS.REPORTS_INVENTARIO_READ);
      normalized.add(FEATURE_PERMISSIONS.REPORTS_FINANCIEROS_READ);
    }
  });

  return resolveSharedEffectivePermissionKeys(Array.from(normalized));
}

export function hasEffectivePermission(
  permissions: readonly string[],
  requiredPermission: string
): boolean {
  return normalizePermissionKeys(permissions).includes(requiredPermission);
}

export function canShowStockMovementHistory(
  permissions: readonly string[]
): boolean {
  return (
    !permissions.includes(STOCK_MOVEMENT_PERMISSION_EXPLICIT) ||
    hasEffectivePermission(
      permissions,
      FEATURE_PERMISSIONS.STOCK_MOVEMENTS_READ
    )
  );
}

/**
 * Get required permission for a route path
 * Supports dynamic segments like [orgSlug] and [id]
 */
export function getRoutePermission(pathname: string): RoutePermission | null {
  // Try exact match first
  if (pathname in ROUTE_PERMISSION_MAP) {
    return ROUTE_PERMISSION_MAP[pathname] || null;
  }

  // Try pattern matching for dynamic routes
  for (const [pattern, permission] of Object.entries(ROUTE_PERMISSION_MAP)) {
    if (matchRoutePattern(pathname, pattern)) {
      return permission;
    }
  }

  // Check for parent route permissions by walking up the path
  // e.g., /[orgSlug]/ventas/nueva/checkout -> /[orgSlug]/ventas/nueva -> /[orgSlug]/ventas
  const segments = pathname.split("/").filter(Boolean);
  while (segments.length > 1) {
    segments.pop();
    const parentPath = `/${segments.join("/")}`;

    if (parentPath in ROUTE_PERMISSION_MAP) {
      return ROUTE_PERMISSION_MAP[parentPath] || null;
    }

    // Also check with pattern matching
    for (const [pattern, permission] of Object.entries(ROUTE_PERMISSION_MAP)) {
      if (matchRoutePattern(parentPath, pattern)) {
        return permission;
      }
    }
  }

  return null;
}

/**
 * Check if a pathname matches a route pattern
 * Handles dynamic segments like [orgSlug] and [id]
 */
function matchRoutePattern(pathname: string, pattern: string): boolean {
  // Convert pattern to regex by escaping special regex chars and replacing dynamic segments
  const regexPattern = pattern
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // Escape regex special characters
    .replace(/\\\[orgSlug\\\]/g, "[^/]+") // Replace \[orgSlug\] with non-slash characters
    .replace(/\\\[id\\\]/g, "[^/]+") // Replace \[id\] with non-slash characters
    .replace(/\\\[.*?\\\]/g, "[^/]+"); // Replace any other dynamic segments

  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(pathname);
}

/**
 * Extract orgSlug from pathname
 * Assumes format: /orgSlug/... or /orgSlug
 */
export function extractOrgSlug(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length > 0 ? segments[0] || null : null;
}

/**
 * Check if route requires authentication (all dashboard routes do)
 */
export function requiresAuth(pathname: string): boolean {
  // All routes starting with /[orgSlug] require authentication
  const orgSlug = extractOrgSlug(pathname);
  return orgSlug !== null && getRoutePermission(pathname) !== null;
}
